import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { DocManiaLogo, DocManiaLogoImage } from '../components/DocManiaLogo';
import { FileText, Mail, Lock, Loader2 } from 'lucide-react';
import axios from 'axios';
import { getPostLoginRedirect } from '../utils/postLoginRedirect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowResend(false);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.full_name}!`);
      navigate(getPostLoginRedirect(location.search));
    } catch (error) {
      const msg = error.response?.data?.detail || 'Login failed';
      toast.error(msg);
      // Show resend option if email not verified
      if (msg.includes('verify your email')) {
        setShowResend(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    setResendLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email });
      toast.success('Verification email sent. Please check your inbox.');
      setShowResend(false);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send verification email';
      toast.error(msg);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-brand-hero">
      <Card className="w-full max-w-md card-elevated animate-fade-in">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <DocManiaLogoImage size={56} className="shadow-md" />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold">Welcome Back</CardTitle>
            <CardDescription className="text-muted-foreground">
              Sign in to your <DocManiaLogo size="small" /> account
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline font-medium"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            
            <Button
              type="submit"
              data-testid="login-submit-btn"
              className="w-full glow-button"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          {showResend && (
            <div className="mt-4 p-4 rounded-lg bg-destructive/5 border border-destructive/20">
              <p className="text-sm text-foreground mb-3">
                Please verify your email before logging in.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="w-full"
                data-testid="resend-verification-btn"
              >
                {resendLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Resend Verification Email'
                )}
              </Button>
            </div>
          )}
          
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary hover:underline font-medium" data-testid="signup-link">
              Create one
            </Link>
          </div>

          <p className="mt-4 text-center text-xs text-muted-foreground leading-relaxed" data-testid="login-legal-text">
            By continuing, you agree to our{' '}
            <a href="https://www.docmania.co/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="login-terms-link">Terms &amp; Conditions</a>
            {' '}and{' '}
            <a href="https://www.docmania.co/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="login-privacy-link">Privacy Policy</a>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

